import { SlotRendering } from '@frontend/common-types';
import { CommonMessage } from '@frontend/lang';
import { SlotStatus, SlotType } from '@frontend/slot/types';
import React from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';

import { useSlotController } from './slot.controller';

interface SlotProps<TSlot extends SlotRendering = SlotRendering> {
    slot: TSlot;
    scale: number;
    offsetX: number;
    offsetY: number;
    onSlotClick?: (clickedSlot: TSlot) => void;
    showLabels?: boolean;
    enableHover?: boolean;
    hoverElement?: (slot: TSlot) => React.ReactNode;
    getSlotColor?: (slot: TSlot) => string;
    renderElement?: (slot: TSlot) => React.ReactNode;
}

export const Slot = <TSlot extends SlotRendering = SlotRendering>({
    slot,
    scale,
    offsetX,
    offsetY,
    onSlotClick,
    showLabels = true,
    enableHover = true,
    hoverElement,
    getSlotColor: getSlotColorOverride,
    renderElement
}: SlotProps<TSlot>) => {
    const controller = useSlotController(slot, scale, offsetX, offsetY, getSlotColorOverride);

    if (!controller) {
        return null;
    }

    const {
        slotRef,
        isHovered,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseMove,
        colorClass,
        slotLeft,
        slotTop,
        slotWidth,
        slotHeight,
        floatingRefs,
        floatingStyles,
        label
    } = controller;

    const handleClick = () => {
        if (onSlotClick) {
            onSlotClick(slot);
        }
    };

    function render() {
        if (renderElement) {
            return renderElement(slot);
        }
        if (!showLabels) {
            return;
        }
        if (slot.type === SlotType.DROP_STATION && slot.status === SlotStatus.AVAILABLE) {
            return (
                <>
                    <div className='text-background-surface pointer-events-none absolute inset-0 flex items-center justify-center p-1 text-center text-xs leading-tight font-semibold select-none'>
                        {CommonMessage.OBJECTS.SLOT.DROP_OFF_OPENING}
                    </div>
                    <div className='text-background-surface pointer-events-none absolute bottom-0 left-0 pb-1 pl-2 text-xs font-semibold select-none'>
                        {label}
                    </div>
                </>
            );
        }
        return <div className='text-background-surface pointer-events-none absolute bottom-0 left-0 pb-1 pl-2 text-xs font-semibold select-none'>{label}</div>;
    }
    return (
        <>
            <div
                ref={slotRef}
                onClick={handleClick}
                onMouseEnter={enableHover ? handleMouseEnter : undefined}
                onMouseLeave={enableHover ? handleMouseLeave : undefined}
                onMouseMove={enableHover ? handleMouseMove : undefined}
                className={`absolute transition-opacity hover:opacity-90 ${colorClass}`}
                style={{
                    left: `${slotLeft}px`,
                    top: `${slotTop}px`,
                    width: `${slotWidth}px`,
                    height: `${slotHeight}px`,
                    cursor: onSlotClick ? 'pointer' : 'default'
                }}>
                {render()}
            </div>
            {enableHover &&
                isHovered &&
                createPortal(
                    <div
                        ref={floatingRefs.setFloating}
                        style={floatingStyles}
                        className='z-50'>
                        {hoverElement ? (
                            <div className='bg-background-surface outline-border-default inline-flex w-fit min-w-50 flex-col items-start justify-start gap-5 rounded-2xl p-6 outline-1 outline-offset-[-1px]'>
                                <div className='text-foreground-default justify-start text-lg font-semibold'>
                                    {CommonMessage.OBJECTS.SLOT.DEFAULT} {slot.display}
                                </div>
                                {hoverElement(slot)}
                            </div>
                        ) : (
                            <div className='bg-background-surface outline-border-default inline-flex w-fit min-w-50 flex-col items-start justify-start gap-5 rounded-2xl p-6 outline-1 outline-offset-[-1px]'>
                                <div className='text-foreground-default justify-start text-lg font-semibold'>
                                    {CommonMessage.OBJECTS.SLOT.DEFAULT} {slot.display}
                                </div>
                                <div className='text-foreground-subtle text-xs'>
                                    {CommonMessage.OBJECTS.DIMENSION.PLURAL}: {slot.dimension?.width} × {slot.dimension?.height}
                                </div>
                                {slot.type && (
                                    <div className='text-foreground-subtle text-xs'>
                                        {CommonMessage.OBJECTS.COMMON_FIELDS.TYPE}: {slot.type}
                                    </div>
                                )}
                                {slot.status && (
                                    <div className='text-foreground-subtle text-xs'>
                                        {CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}: {slot.status}
                                    </div>
                                )}
                                {slot.occupied && (
                                    <div className='text-foreground-subtle text-xs'>
                                        <FormattedMessage
                                            defaultMessage='Occupied'
                                            id='spotRendering.slot.occupied'
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>,
                    document.body
                )}
        </>
    );
};
